import { useState, useEffect, useRef, useCallback } from 'react';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';

// ─────────────────────────────────────────────────────────────────────────────
// «جست‌وجوی بیمار» — looking someone up.
//
// The BFF has offered `patients(filter, search, clinicId)` all along and NOTHING
// called it. With no Encounters screen on the phone either, a doctor holding
// only a phone could open exactly one thing: today's queue. If the patient in
// front of them was not on today's list — a walk-in, yesterday's follow-up, a
// phone call about a result — there was no way to reach the record at all.
//
// ── Two deliberate limits ───────────────────────────────────────────────────
// TWO CHARACTERS before anything is sent. One character matches most of a
// clinic and costs a round trip to say so.
//
// THE QUERY IS UNPAGED, so the result is capped HERE. A clinic with ten
// thousand patients and a doctor who types «م» would otherwise hand the phone a
// list it cannot render. The cap is a client-side truth the screen states out
// loud rather than a silent truncation; pagination is a BFF follow-up if real
// clinics make it necessary.
//
// ── No PHI in the URL ───────────────────────────────────────────────────────
// The query stays in component state, never in a query parameter. A patient's
// name in an address is PHI in the browser history, in a shared link and in
// every proxy log between here and the BFF — which is exactly why the desktop
// Encounters screen keeps free-text search out of the URL too.
// ─────────────────────────────────────────────────────────────────────────────

const SEARCH_QUERY = `
  query SearchPatients($search: String, $clinicId: ID) {
    patients(search: $search, clinicId: $clinicId) {
      id
      name
      phone
      nationalId
      age
      gender
      status
      fileNumber
      lastEncounter
      encountersCount
    }
  }
`;

/** Below this, nothing is sent. */
export const MIN_QUERY_LENGTH = 2;

/** How many results a phone will render before saying there are more. */
export const RESULT_CAP = 50;

const DEBOUNCE_MS = 300;

export function usePatientSearch({ clinicId = null } = {}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // `searched` separates «nothing matched» from «nothing asked yet» — without
  // it an untouched screen would render the no-results empty state.
  const [searched, setSearched] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const debounceRef = useRef(null);
  // Every request carries a token; a late answer to an abandoned query is
  // dropped rather than overwriting the current one.
  const tokenRef = useRef(0);

  const run = useCallback((term) => {
    const token = ++tokenRef.current;
    setLoading(true);
    setError(null);
    bffQuery(SEARCH_QUERY, { search: term, clinicId: clinicId || undefined })
      .then((data) => {
        if (token !== tokenRef.current) return;
        const all = data?.patients || [];
        setResults(all.slice(0, RESULT_CAP));
        setTruncated(all.length > RESULT_CAP);
        setSearched(true);
      })
      .catch((err) => {
        if (token !== tokenRef.current) return;
        setError(err);
        setResults([]);
        setSearched(true);
      })
      .finally(() => {
        if (token === tokenRef.current) setLoading(false);
      });
  }, [clinicId]);

  const search = useCallback((value) => {
    setQuery(value);
    clearTimeout(debounceRef.current);

    if (value.trim().length < MIN_QUERY_LENGTH) {
      // Abandon anything in flight so its answer cannot land under a query
      // that is no longer long enough to have one.
      tokenRef.current += 1;
      setResults([]);
      setSearched(false);
      setTruncated(false);
      setLoading(false);
      setError(null);
      return;
    }

    debounceRef.current = setTimeout(() => run(value.trim()), DEBOUNCE_MS);
  }, [run]);

  const clear = useCallback(() => search(''), [search]);

  /** The one action the error state offers. */
  const retry = useCallback(() => {
    const term = query.trim();
    if (term.length >= MIN_QUERY_LENGTH) run(term);
  }, [query, run]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const belowMinimum = query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH;

  return {
    query, search, clear, retry,
    results, loading, error, searched, truncated,
    belowMinimum,
    /** 'idle' | 'below' | 'loading' | 'error' | 'empty' | 'content'. */
    state: query.trim().length === 0 ? 'idle'
      : belowMinimum ? 'below'
        : error ? 'error'
          : loading && results.length === 0 ? 'loading'
            : searched && results.length === 0 ? 'empty'
              : 'content',
  };
}

export default usePatientSearch;
